import { Text as T } from '@eo-locale/preact';
import { useMemo } from 'preact/hooks';
import { ReviewSorting, VerificationStatus } from 'types/review';
import { ExtendedFullPageWidgetSettings } from 'types/settings';
import { WidgetRequest } from 'types/widget';
import { useReviews } from 'utilities/api';
import { useDebounce } from 'utilities/use-debounce';
import { usePage } from 'utilities/use-page';

import { Pagination } from 'components/Pagination';
import { useSettings } from 'components/SettingsProvider';
import { Spinner } from 'components/Spinner';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';

import { MasonryLayout } from './components/MasonryLayout';
import { SkeletonContent } from './components/SkeletonContent';

import styles from './FullPageWidget.module.scss';

interface FullPageWidgetProps {
  onRemove: () => void;
}

export function FullPageWidget({ onRemove }: FullPageWidgetProps) {
  const {
    reviews_per_page,
    total_reviews,
    product_ids,
    filter_by_min_rating,
    filter_by_verified,
    filter_with_images,
  } = useSettings<ExtendedFullPageWidgetSettings>();

  const { page, handlePage, handlePrevPage, handleNextPage } = usePage(1);

  let count = reviews_per_page;

  if (total_reviews < reviews_per_page) {
    count = total_reviews;
  }

  const request = useMemo<WidgetRequest>(
    () => ({
      components: {
        images: null,
        rating_details: null,
      },
      filters: {
        has_images: filter_with_images ? true : null,
        product_ids,
        rating_from: filter_by_min_rating,
        rating_to: 5,
        verified: filter_by_verified ? VerificationStatus.VERIFIED : null,
      },
      count,
      page,
      sort: ReviewSorting.DATE_DESC,
    }),
    [
      product_ids,
      filter_by_min_rating,
      filter_by_verified,
      filter_with_images,
      count,
      page,
    ],
  );

  const debouncedRequest = useDebounce<WidgetRequest>(request, 250);

  const { reviews, meta, isLoading, isFetching } = useReviews(debouncedRequest);

  if (isLoading) {
    return <SkeletonContent />;
  }

  if (reviews.length === 0) onRemove();

  let total = meta.total;

  if (total >= total_reviews) {
    total = total_reviews;
  }

  const totalPages = Math.ceil(total / meta.count);

  const paginationMarkup = totalPages > 1 && (
    <div className={styles.PaginationWrapper}>
      <Stack distribution="center">
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          hasNext={page < totalPages}
          hasPrev={page > 1}
          onPrev={handlePrevPage}
          onNext={handleNextPage}
          onPage={handlePage}
        />
      </Stack>
    </div>
  );

  const spinnerMarkup = isFetching && (
    <div className={styles.LoadingOverlay}>
      <Spinner />
    </div>
  );

  const reviews_list = [...reviews];

  if (meta.page === totalPages) {
    const lastPageReviewsCount = total - (totalPages - 1) * count;

    if (lastPageReviewsCount < count) {
      reviews_list.length = lastPageReviewsCount;
    }
  }

  const reviewsMarkup = !isFetching ? (
    <MasonryLayout reviews={reviews_list} />
  ) : null;

  return reviews_list.length > 0 ? (
    <section className={styles.FullPageWidget}>
      <div className={styles.Header}>
        <Text size="extraLarge" as="p" variation="strong">
          <T
            id="widget_full_page_title"
            defaultMessage="Reviews from our customers"
          />
        </Text>
      </div>
      {reviewsMarkup}
      {paginationMarkup}
      {spinnerMarkup}
    </section>
  ) : null;
}
